import {
  HeatMapOutlined,
  LineChartOutlined,
  MenuUnfoldOutlined,
  RobotOutlined,
  SendOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Drawer, Input, List } from 'antd';
import React, { useEffect, useRef } from 'react';
import { ResponsiveContainer } from 'recharts';
import styled, { keyframes } from 'styled-components';
import { useCustomTranslation } from '../../hooks/custom-translation';
import { MapsProvider } from '../../hooks/maps';
import { useChatClient } from '../../hooks/useChatClient';
import { ChatMessage, ChatStoreProvider } from '../../hooks/useChatStore';
import Map from '../../pages/FilterMap/Map';
import Wrapper from '../GoogleMaps/Wrapper';
import ChartAI from './ChartAI';

interface ModalSalesboxAIProps {
  visible: boolean;
  sessionId: string | null;
  indexName: string;
  personType: 'PF' | 'PJ';
  onCancel: () => void;
}

const DrawerContentWrapper = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
`;

const ChatContainer = styled.div`
  flex: 0 0 600px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  height: 100%;
  box-sizing: border-box;
`;

const MessageChartWrapper = styled.div`
  width: 100%;
`;

const MessageListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
`;

const MessageList = styled(List)`
  padding: 12px;
`;

const MessageItem = styled(List.Item)<{ $isUser: boolean }>`
  display: flex;
  flex-direction: ${p => (p.$isUser ? 'row-reverse' : 'row')};
  margin-bottom: 24px;
  align-items: flex-start;
  gap: 8px;
  animation: fadeIn 0.3s ease-in-out;
  justify-content: ${p => (p.$isUser ? 'flex-end' : 'flex-start')};

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledAvatar = styled(Avatar)<{ $isUser: boolean }>`
  background: ${p => (p.$isUser ? '#1890ff' : '#722ed1')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  flex-shrink: 0;
  margin-top: 4px;

  &:hover {
    transform: scale(1.05);
  }
`;

const MessageContent = styled.div<{ $isUser: boolean }>`
  white-space: pre-wrap;
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${p => (p.$isUser ? '#1890ff' : 'white')};
  color: ${p => (p.$isUser ? 'white' : '#2c3e50')};
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  margin: ${p => (p.$isUser ? '0 0 0 auto' : '0 auto 0 0')};

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    ${p => (p.$isUser ? 'right: -6px;' : 'left: -6px;')}
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    ${p =>
      p.$isUser
        ? 'border-left: 6px solid #1890ff;'
        : 'border-right: 6px solid white;'}
  }
`;

const bounce = keyframes`
  0%, 80%, 100% { 
    transform: translateY(0);
  }
  40% { 
    transform: translateY(-4px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding-left: 16px;
  animation: fadeIn 0.3s ease-in-out;
`;

const LoadingAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #722ed1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: fit-content;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: -8px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid white;
  }

  span {
    width: 6px;
    height: 6px;
    background: #666;
    border-radius: 50%;
    display: inline-block;
    animation: ${bounce} 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;

const InputContainer = styled.div`
  padding: 16px;
  background: white;
  border-top: 1px solid #eaeaea;
  display: flex;
  align-items: flex-end;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
`;

const TextAreaContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-end;
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 12px;
  resize: none;
  padding: 12px 48px 12px 12px;
  border: 1px solid #eaeaea;
  transition: all 0.3s ease;
  max-height: 200px;
  overflow-y: auto;

  &:hover,
  &:focus {
    border-color: #722ed1;
    box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
`;

const SuggestionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SuggestionButton = styled(Button)`
  flex: 1 1 45%;
  min-width: 0;

  height: auto !important;
  min-height: 0;
  padding: 8px 12px !important;

  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;

  display: flex;
  align-items: flex-start;

  background: #f5f5f5;
  border: none;
`;

const CloseChartButton = styled(MenuUnfoldOutlined)`
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 16px;
  color: #888;
  cursor: pointer;
  &:hover {
    color: #555;
  }
`;

const ChartButton = styled(Button)`
  margin-top: 8px;
  margin-bottom: 8px;
  display: flex !important;
  align-items: center !important;
  border-radius: 12px !important;
  padding: 8px 12px !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
  text-align: left !important;
  text-decoration: none !important;
`;

const SendButton = styled(Button)`
  position: absolute;
  right: 8px;
  bottom: 8px;
  border-radius: 12px;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: transparent;
  border: none;
  color: #1890ff;

  &:hover {
    background: rgba(24, 144, 255, 0.1);
    transform: scale(1.05);
  }

  &:disabled {
    color: #d9d9d9;
    background: transparent;
    cursor: not-allowed;
  }
`;

const MapWrapper = styled.div`
  display: flex;
  height: 60vh;
  width: 100%;
`;

const SmoothDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    transition: width 0.3s ease-in-out !important;
  }
`;

const InsightsContainer = styled.div`
  width: 100vw;
  max-width: 100vw;
  min-width: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 24px 12px 12px 12px;
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;
`;

const ModalSalesboxAIContent: React.FC<ModalSalesboxAIProps> = ({
  visible,
  sessionId,
  indexName,
  personType,
  onCancel,
}) => {
  const { tContactManagement } = useCustomTranslation('contactManagement');
  const {
    messages,
    inputValue,
    isLoading,
    setInputValue,
    sendMessage,
    selectedCharts,
    setSelectedCharts,
    setSelectedMap,
    selectedMap,
  } = useChatClient(sessionId || 'default', indexName, personType);
  const messageListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) {
      setInputValue('');
      setSelectedCharts([]);
    }
  }, [visible, setInputValue]);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [isLoading, messages, visible]);

  const getMessageAvatar = (role: string) => {
    switch (role) {
      case 'user':
        return <UserOutlined />;
      case 'assistant':
        return <RobotOutlined />;
      default:
        return <SettingOutlined />;
    }
  };

  function handleSuggestionClick(suggestion: string) {
    sendMessage(suggestion);
  }

  const hasInsights = selectedCharts.length > 0 || selectedMap.length > 0;

  return (
    <SmoothDrawer
      visible={visible}
      onClose={() => {
        setSelectedCharts([]);
        setSelectedMap([]);
        onCancel();
      }}
      closable
      title={tContactManagement('salesbox_ai')}
      width={hasInsights ? '100vw' : 600}
      bodyStyle={{ padding: 0, height: '100%' }}
      style={{ height: '100%' }}
    >
      <DrawerContentWrapper>
        {hasInsights && (
          <InsightsContainer>
            <div style={{ position: 'relative', width: '100%' }}>
              <CloseChartButton
                onClick={() => {
                  setSelectedCharts([]);
                  setSelectedMap([]);
                }}
                title="Fechar gráficos"
                style={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
              />
            </div>
            {selectedMap.length > 0 && (
              <div style={{ marginBottom: 24, position: 'relative' }}>
                {selectedMap.map(mapDef => (
                  <div key={mapDef.id} style={{ marginBottom: 16 }}>
                    <h4>{mapDef.title}</h4>
                    <p style={{ color: '#666', fontSize: 12 }}>
                      {mapDef.description}
                    </p>
                    <MapWrapper>
                      <Wrapper>
                        <MapsProvider>
                          <Map
                            setPosX={() => {}}
                            setPosY={() => {}}
                            preview
                            data={{
                              geoHashs: mapDef.points.map(point => ({
                                geoHash: point.geohash,
                                length: point.value,
                              })) || [{ geoHash: '', length: 0 }],
                            }}
                            center={{
                              lat: -15.7801,
                              lng: -47.9292,
                            }}
                          />
                        </MapsProvider>
                      </Wrapper>
                    </MapWrapper>
                  </div>
                ))}
              </div>
            )}
            {selectedCharts.length > 0 && (
              <div style={{ position: 'relative' }}>
                <ChartsGrid>
                  {selectedCharts.map((chart, i) => (
                    <MessageChartWrapper
                      key={chart.description || chart.title || i}
                    >
                      {chart.title && <h4>{chart.title}</h4>}
                      {chart.description && (
                        <p style={{ color: '#666', fontSize: 12 }}>
                          {chart.description}
                        </p>
                      )}
                      <ResponsiveContainer width="100%" height={400}>
                        <ChartAI chart={chart} />
                      </ResponsiveContainer>
                    </MessageChartWrapper>
                  ))}
                </ChartsGrid>
              </div>
            )}
          </InsightsContainer>
        )}
        <ChatContainer>
          <MessageListContainer ref={messageListRef}>
            <MessageList
              dataSource={messages}
              renderItem={item => {
                const messageItem = item as ChatMessage;
                return (
                  <MessageItem $isUser={messageItem.role === 'user'}>
                    <StyledAvatar
                      $isUser={messageItem.role === 'user'}
                      icon={getMessageAvatar(messageItem.role)}
                    />
                    <MessageContent $isUser={messageItem.role === 'user'}>
                      {messageItem.content}
                      {messageItem.chartAI && messageItem.chartAI.length > 0 && (
                        <ChartButton
                          onClick={() =>
                            setSelectedCharts(messageItem.chartAI!)
                          }
                        >
                          <LineChartOutlined />
                          Ver gráfico
                        </ChartButton>
                      )}
                      {messageItem.mapAI && messageItem.mapAI.length > 0 && (
                        <ChartButton
                          onClick={() => setSelectedMap(messageItem.mapAI!)}
                        >
                          <HeatMapOutlined />
                          Ver mapa
                        </ChartButton>
                      )}
                      {messageItem.suggestions &&
                        messageItem.suggestions.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                              Sugestões:
                            </div>
                            <SuggestionsWrapper>
                              {messageItem.suggestions.map(suggestion => (
                                <SuggestionButton
                                  key={suggestion}
                                  size="small"
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                >
                                  {suggestion}
                                </SuggestionButton>
                              ))}
                            </SuggestionsWrapper>
                          </div>
                        )}
                    </MessageContent>
                  </MessageItem>
                );
              }}
            />
            {isLoading && (
              <LoadingContainer>
                <LoadingAvatar>
                  <RobotOutlined style={{ color: 'white', fontSize: '20px' }} />
                </LoadingAvatar>
                <LoadingDots>
                  <span />
                  <span />
                  <span />
                </LoadingDots>
              </LoadingContainer>
            )}
          </MessageListContainer>
          <InputContainer>
            <TextAreaContainer>
              <StyledTextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Digite sua mensagem..."
              />
              <SendButton
                type="text"
                icon={<SendOutlined />}
                onClick={() => sendMessage()}
                disabled={!inputValue.trim()}
              />
            </TextAreaContainer>
          </InputContainer>
        </ChatContainer>
      </DrawerContentWrapper>
    </SmoothDrawer>
  );
};

const ModalSalesboxAI: React.FC<ModalSalesboxAIProps> = ({
  visible,
  sessionId,
  indexName,
  personType,
  onCancel,
}) => {
  return (
    <ChatStoreProvider>
      <ModalSalesboxAIContent
        visible={visible}
        sessionId={sessionId}
        indexName={indexName}
        personType={personType}
        onCancel={onCancel}
      />
    </ChatStoreProvider>
  );
};

export default ModalSalesboxAI;
